import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Form, Tab, Tabs, Table } from "react-bootstrap";
import swal from "sweetalert";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../../../assets/table-style";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../../../../assets/global/url";
import Select from "react-select";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import NoAccess from "../../../../../assets/img/NoAccess.png";
import { useQueryParams } from "../../../../../hooks/customHook/useQueryParams";

const NewBalanceSheet = ({ authrztn }) => {
  // For query parameters
  const queryParameterList = ["module", "id", "fromDate", "toDate"];
  const queryParameters = useQueryParams(queryParameterList);
  const { module, id, fromDate, toDate } = queryParameters;

  // For cutoff filter
  const [thisFromdate, setThisFromdate] = useState("");
  const [thisTodate, setThisTodate] = useState("");
  const [cutoffList, setCutoffList] = useState([]);
  const [selectedCutoff_id, setSelectedCutoff_id] = useState("");

  // States per section
  const [assets, setAssets] = useState({});
  const [nonCurrentAssets, setNonCurrentAssets] = useState({});
  const [liabilities, setLiabilities] = useState({});
  const [ownersEquity, setOwnersEquity] = useState({});

  // Helper: format number to two decimal places
  const formatToTwoDecimal = (num) => {
    const value = num || 0;
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      style: "currency",
      currency: "PHP",
    });
  };

  // Get the default cutoff and initialize states
  const getCutoff = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/cutoff/getCutoffs`);
      if (!res.data.length) return; // No cutoff records yet

      const [{ id: cutoffId, from: startDate, to: endDate }] = res.data; // Destructure the first item in array

      // Use query parameters if provided; otherwise, fall back to default values
      const defaultCutoffId = id || cutoffId;
      const cutoffStartDate = fromDate || startDate;
      const cutoffEndDate = toDate || endDate;
      const cutoffInfo = [defaultCutoffId, cutoffStartDate, cutoffEndDate];

      setCutoffList(res.data);
      setSelectedCutoff_id(defaultCutoffId);
      setThisFromdate(cutoffStartDate);
      setThisTodate(cutoffEndDate);
      getFinalBalanceSheet(...cutoffInfo);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch the data for different section of balance sheet
  const getFinalBalanceSheet = async (id, startDate, endDate) => {
    try {
      const balanceSheetEndpoint = `${BASE_URL}/balance_sheet`;

      const params = {
        startDate,
        endDate,
      };

      const [assets, liabilities, equities] = await Promise.all([
        axios.get(`${balanceSheetEndpoint}/assets`, { params }),
        axios.get(`${balanceSheetEndpoint}/liabilities`, { params }),
        axios.get(`${balanceSheetEndpoint}/equities`, { params }),
      ]);

      // Refactor to one usestate
      setAssets(assets.data);
      setLiabilities(liabilities.data);
      setOwnersEquity(equities.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Handle change cutoff
  const handleCutoffChange = (value) => {
    // Ensure dates are strings, use empty string as fallback
    const cutoff_fromdate =
      cutoffList.find((cutoff) => String(cutoff.id) === String(value)).from ||
      "";
    const cutoff_todate =
      cutoffList.find((cutoff) => String(cutoff.id) === String(value)).to || "";

    setSelectedCutoff_id(value);
    setThisFromdate(cutoff_fromdate);
    setThisTodate(cutoff_todate);
    getFinalBalanceSheet(value, cutoff_fromdate, cutoff_todate);
  };

  useEffect(() => {
    getCutoff();
  }, []);

  const exportToExcel = () => {
    // Get the selected cutoff details
    const selectedCutoff = cutoffList.find(
      (cutoff) => cutoff.id === selectedCutoff_id,
    );
    const currentCutoffName = selectedCutoff ? selectedCutoff.name : "";

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Prepare the data to match your table structure exactly
    const sheetData = [
      // Header with cutoff information
      ["BALANCE SHEET"],
      [`Cutoff: ${currentCutoffName} (${thisFromdate} to ${thisTodate})`],
      [], // Empty row

      // Assets Section
      ["FIXED ASSET SECTION"],
      [],
      ["ASSETS", "", ""],
      ["Subject 1", "Subject 2", "Amount (₱)"],

      // Current Assets
      [
        "Current Assets",
        "Cash and Cash Equivalents",
        assets?.totalFundsAmount || 0,
      ],
      ["", "Accounts Receivable", assets?.totalAccountsReceivable || 0],
      [
        "",
        "",
        (assets?.totalFundsAmount || 0) +
          (assets?.totalAccountsReceivable || 0),
      ],

      // Inventory
      [
        "Inventory",
        "",
        assets?.totalStockManagement?.reduce(
          (sum, data) => sum + data.total_stock * data.average_price || 0,
          0,
        ) || 0,
      ],
      [
        "",
        "",
        assets?.totalStockManagement?.reduce(
          (sum, data) => sum + data.total_stock * data.average_price || 0,
          0,
        ) || 0,
      ],

      // Prepaid Expenses
      ["Prepaid Expenses", "", assets?.totalPrepaidExpenses || 0],
      ["", "", assets?.totalPrepaidExpenses || 0],

      // Net Fixed Asset
      [
        "Net Fixed Asset",
        "Fixed Asset Original Values",
        assets?.totalFixedAssetOriginalValues || 0,
      ],
      [
        "",
        "Cumulative Discount",
        assets?.totalFixedAssetCummulativeDiscount || 0,
      ],
      ["", "Impairment of Fixed Asset", assets?.totalFixedAssetImpairment || 0],
      [
        "",
        "",
        (assets?.totalFixedAssetImpairment || 0) +
          (assets?.totalFixedAssetCummulativeDiscount || 0),
      ],

      // Other Current Assets
      ["Other Current Assets", "", assets?.totalOtherCurrentAssets || 0],
      ["", "", assets?.totalOtherCurrentAssets || 0],

      // Total Current Assets
      [
        "TOTAL CURRENT ASSETS",
        "",
        (assets?.totalOtherCurrentAssets || 0) +
          (assets?.totalPrepaidExpenses || 0) +
          (assets?.totalStockManagement?.reduce(
            (sum, data) => sum + data.total_stock * data.average_price || 0,
            0,
          ) || 0) +
          (assets?.totalAccountsReceivable || 0) +
          (assets?.totalFundsAmount || 0) +
          (assets?.totalFixedAssetCummulativeDiscount || 0) +
          (assets?.totalFixedAssetImpairment || 0),
      ],

      [], // Empty row

      // Liabilities Section
      ["LIABILITIES SECTION"],
      [],
      ["LIABILITIES", "", ""],
      ["Subject 1", "Subject 2", "Amount (₱)"],

      // Current Liabilities
      [
        "Current Liabilities",
        "Bank Payable",
        liabilities?.OverAllTotalBankTransactions || 0,
      ],
      ["", "Purchase Payable", liabilities?.totalPayable || 0],
      ["", "Expenses Payable", liabilities?.totalExpenses || 0],
      ["", "Loans", liabilities?.totalLoanBalance || 0],
      [
        "",
        "",
        (liabilities?.OverAllTotalBankTransactions || 0) +
          (liabilities?.totalPayable || 0) +
          (liabilities?.totalExpenses || 0),
      ],

      // Short-Term Debt
      ["Short-Term Debt", "", 0],
      ["", "", 0],

      // Accrued Liabilities
      ["Accrued Liabilities", "", 0],
      ["", "", 0],

      // Other Current Liabilities
      [
        "Other Current Liabilities",
        "",
        liabilities?.totalOtherCurrentLiabilities || 0,
      ],
      ["", "", liabilities?.totalOtherCurrentLiabilities || 0],

      // Total Current Liabilities
      [
        "TOTAL CURRENT LIABILITIES",
        "",
        (liabilities?.OverAllTotalBankTransactions || 0) +
          (liabilities?.totalPayable || 0) +
          (liabilities?.totalExpenses || 0) +
          (liabilities?.totalOtherCurrentLiabilities || 0),
      ],

      [], // Empty row

      // Owners Equity Section
      ["OWNERS EQUITY SECTION"],
      [],
      ["OWNERS EQUITY", "", ""],
      ["Subject 1", "Amount (₱)"],

      // Capital
      ["Capital", ownersEquity?.totalOwnersEquity || 0],
      ["", ownersEquity?.totalOwnersEquity || 0],

      // Profit Distribution
      ["Profit Distribution", ownersEquity?.retainedEarnings || 0],
      ["", ownersEquity?.retainedEarnings || 0],

      // Total Equity
      [
        "TOTAL EQUITY",
        (ownersEquity?.totalOwnersEquity || 0) +
          (ownersEquity?.retainedEarnings || 0),
      ],
    ];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Set column widths
    ws["!cols"] = [
      { wch: 30 }, // Subject 1 column
      { wch: 30 }, // Subject 2 column
      { wch: 20 }, // Amount column
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Balance Sheet");

    // Generate file and download
    const fileName = `Balance_Sheet_${currentCutoffName.replace(
      /\s+/g,
      "_",
    )}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="h-100 w-100 border bg-white custom-container balance-sheet-page">
      {authrztn.includes("Reporting-View") ? (
        <>
          <div className="w-100 p-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3 text-uppercase">
                {" "}
                {module && id && fromDate && toDate && module === "cutoff" && (
                  <Link
                    to={`/accounting/view-cutoff/${id}`}
                    className="text-dark mx-2"
                  >
                    <i class="fa-solid fa-arrow-left"></i>
                  </Link>
                )}
                {module && id && module === "earnings" && (
                  <Link
                    to={`/accounting/view-earnings/${id}`}
                    className="text-dark mx-2"
                  >
                    <i class="fa-solid fa-arrow-left"></i>
                  </Link>
                )}
                Final Balance Sheet
              </span>
            </div>
            <div>
              {authrztn.includes("Reporting-IE") && (
                <Button
                  variant="success"
                  onClick={exportToExcel}
                  className="ms-2"
                >
                  Export to Excel
                </Button>
              )}
            </div>
          </div>

          <div className="w-100 row mx-auto mt-2">
            <h6>Accounting Period</h6>
            <div className="col-sm mb-2">
              <span>Cutoff</span>
              <Form.Select
                value={selectedCutoff_id}
                onChange={(e) => handleCutoffChange(e.target.value)}
                className="form-select"
                onMouseDown={(e) => {
                  if (cutoffList.length === 0) {
                    e.preventDefault();
                    swal({
                      icon: "warning",
                      title: "No Cutoff Found",
                      text: "No cutoff records found. Please create a cutoff first in Monthly Cutoff Module.",
                    });
                    return;
                  }
                }}
              >
                {cutoffList.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-sm mb-2">
              <span>From</span>
              <DatePicker
                selected={thisFromdate}
                dateFormat="MMM/dd/yyyy"
                className="form-control"
                readOnly
              />
            </div>
            <div className="col-sm mb-2">
              <span>To</span>
              <DatePicker
                selected={thisTodate}
                dateFormat="MMM/dd/yyyy"
                className="form-control"
                readOnly
              />
            </div>
            <div className="col-sm"></div>
          </div>

          <div className="container-fluid mt-2">
            <div className="container-fluid">
              <div className="row">
                {/* Assets Section */}
                <div className="w-100 d-flex align-items-center mt-4">
                  <h4 className="text-success">Fixed Asset Section</h4>
                  <hr className="flex-grow-1 mx-3" />
                </div>
                <div className="col-sm mb-3">
                  <div className="w-100 d-flex align-items-center mt-4">
                    <h5>Assets</h5>
                    <hr className="flex-grow-1 mx-3" />
                  </div>
                  {/* Fixed Asset Data */}
                  <div className="w-100">
                    <table className="table table-bordered mt-4">
                      <thead>
                        <tr>
                          <th>Subject 1</th>
                          <th>Subject 2</th>
                          <th className="text-end">Amount (₱)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Current Assets */}
                        <tr>
                          <td rowSpan="4">Current Assets</td>
                          <td>Cash and Cash Equivalents</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              assets?.currentAssets?.cashAndCashEquivalents,
                            )}
                          </td>
                        </tr>

                        <tr>
                          <td>Accounts Receivable</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              assets?.currentAssets?.accountsReceivable,
                            )}
                          </td>
                        </tr>

                        <tr>
                          <td>Receivable Check</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              assets?.currentAssets?.receivableCheck,
                            )}
                          </td>
                        </tr>

                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              {formatToTwoDecimal(assets?.currentAssets?.total)}
                            </strong>
                          </td>
                        </tr>

                        {/* Inventory */}
                        <tr>
                          <td rowSpan="2">Inventory</td>
                          <td></td>
                          <td className="text-end">
                            {formatToTwoDecimal(assets?.inventory?.value)}
                          </td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              {formatToTwoDecimal(assets?.inventory?.total)}
                            </strong>
                          </td>
                        </tr>

                        {/* Net fixed asset */}
                        <tr>
                          <td rowSpan="4">Net Fixed Asset</td>
                          <td>Fixed Asset Original Values</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              assets?.netFixedAsset?.fixedAssetOriginalValues,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Cumulative Discount</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              assets?.netFixedAsset?.cumulativeDiscount,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Impairment of Fixed Asset</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              assets?.netFixedAsset?.impairmentFixedAsset,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              {formatToTwoDecimal(assets?.netFixedAsset?.total)}
                            </strong>
                          </td>
                        </tr>

                        {/* Other Current Assets */}
                        <tr>
                          <td rowSpan="2">Other Current Assets</td>
                          <td></td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              assets?.otherCurrentAssets?.value,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              {formatToTwoDecimal(
                                assets?.otherCurrentAssets?.total,
                              )}
                            </strong>
                          </td>
                        </tr>

                        {/* Total Current Assets */}
                        <tr className="table-primary">
                          <td colSpan="2">
                            <strong>Total Current Assets</strong>
                          </td>
                          <td className="text-end">
                            <strong>
                              {formatToTwoDecimal(assets?.totalCurrentAssets)}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Non-Current Assets Section */}
                <div className="d-none col-sm mb-3">
                  <div className="w-100 d-flex align-items-center mt-4">
                    <h5>Non-Current Assets</h5>
                    <hr className="flex-grow-1 mx-3" />
                  </div>
                  <div className="w-100">
                    <table className="table table-bordered mt-4">
                      <thead>
                        <tr>
                          <th>Subject 1</th>
                          <th>Subject 2</th>
                          <th className="text-end">Amount (₱)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Property, Plant, and Equipment (PP&E) */}
                        <tr>
                          <td rowSpan="2">
                            Property, Plant, and Equipment (PP&E)
                          </td>
                          <td></td>
                          <td className="text-end">0</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              0
                            </strong>
                          </td>
                        </tr>

                        {/* Net Fixed Asset */}
                        <tr>
                          <td rowSpan="4">Net Fixed Asset</td>
                          <td>Fixed Asset Original Values </td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              nonCurrentAssets?.netFixedAsset
                                ?.fixedAssetOriginalValues,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Cumulative Discount</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              nonCurrentAssets?.netFixedAsset
                                ?.cumulativeDiscount,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Impairment of Fixed Asset</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              nonCurrentAssets?.netFixedAsset
                                ?.impairmentFixedAsset,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              {formatToTwoDecimal(
                                nonCurrentAssets?.netFixedAsset?.total,
                              )}
                            </strong>
                          </td>
                        </tr>

                        {/* Intangible Assets
                         */}
                        <tr>
                          <td rowSpan="2">Intangible Assets</td>
                          <td></td>
                          <td className="text-end">0.00</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              0.00
                            </strong>
                          </td>
                        </tr>

                        {/* Investments
                         */}
                        <tr>
                          <td rowSpan="2">Investments</td>
                          <td></td>
                          <td className="text-end">0.00</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              0.00
                            </strong>
                          </td>
                        </tr>

                        {/* Other Non-Current Assets */}
                        <tr>
                          <td rowSpan="2">Other Non-Current Assets</td>
                          <td></td>
                          <td className="text-end">0.00</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              0.00
                            </strong>
                          </td>
                        </tr>

                        {/* Total Non-Current Assets */}
                        <tr className="table-primary">
                          <td colSpan="2">
                            <strong>Total Non-Current Assets</strong>
                          </td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              nonCurrentAssets?.totalNonCurrentAssets,
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="container-fluid">
              <div className="row">
                <div className="w-100 d-flex align-items-center mt-4">
                  <h4 className="text-success">Liabilities Section</h4>
                  <hr className="flex-grow-1 mx-3" />
                </div>
                {/* liabilities Section */}
                <div className="col-sm mb-3">
                  <div className="w-100 d-flex align-items-center mt-4">
                    <h5>Liabilities</h5>
                    <hr className="flex-grow-1 mx-3" />
                  </div>
                  {/* Fixed Asset Data */}
                  <div className="w-100">
                    <table className="table table-bordered mt-4">
                      <thead>
                        <tr>
                          <th>Subject 1</th>
                          <th>Subject 2</th>
                          <th className="text-end">Amount (₱)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Current Liabilities */}
                        <tr>
                          <td rowSpan="5">Accounts Payable</td>
                          {/* <td>Bank Payable</td>
                          <td className="text-end">
                            {liabilities?.OverAllTotalBankTransactions
                              ? liabilities?.OverAllTotalBankTransactions.toLocaleString(
                                  "en-US",
                                  {
                                    style: "currency",
                                    currency: "PHP",
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2,
                                  }
                                )
                              : "₱0.00"}
                          </td> */}
                        </tr>
                        <tr>
                          <td>Issued Check Payable</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              liabilities?.accountsPayable?.issuedCheckPayable,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Purchase Payable</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              liabilities?.accountsPayable?.purchasePayable,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Expenses Payable</td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              liabilities?.accountsPayable?.expensePayable,
                            )}
                          </td>
                        </tr>
                        <tr>
                          {/* <td>Loans</td>
                          <td className="text-end">
                            {" "}
                            {liabilities?.totalLoanBalance
                              ? liabilities?.totalLoanBalance.toLocaleString(
                                  "en-US",
                                  {
                                    style: "currency",
                                    currency: "PHP",
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2,
                                  }
                                )
                              : "₱0.00"}
                          </td> */}
                        </tr>
                        <tr>
                          <td></td>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success">
                              {formatToTwoDecimal(
                                liabilities?.accountsPayable?.total,
                              )}
                            </strong>
                          </td>
                        </tr>
                        {/* Short-Term Debt */}
                        {/* <tr>
                          <td rowSpan="2">Short-Term Debt</td>
                          <td></td>
                          <td className="text-end peso">0.00</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount peso">
                              0.00
                            </strong>
                          </td>
                        </tr> */}
                        {/* Accrued Liabilities
                         */}
                        {/* <tr>
                          <td rowSpan="2">Accrued Liabilities</td>
                          <td></td>
                          <td className="text-end peso">0.00</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount peso">
                              0.00
                            </strong>
                          </td>
                        </tr> */}

                        {/* commented kasi unbalance yung sa fixed asset migration */}
                        {/* Impairment of Fixed Asset */}
                        {/* <tr>
                          <td rowSpan="2">Impairment of Fixed Asset</td>
                          <td></td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              liabilities?.impairmentFixedAsset?.value
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              {formatToTwoDecimal(
                                liabilities?.impairmentFixedAsset?.total
                              )}
                            </strong>
                          </td>
                        </tr> */}

                        {/*  Other Current Liabilities */}
                        <tr>
                          <td rowSpan="2"> Other Current Liabilities</td>
                          <td></td>
                          <td className="text-end">
                            {formatToTwoDecimal(
                              liabilities?.otherCurrentLiabilities?.value,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount">
                              {formatToTwoDecimal(
                                liabilities?.otherCurrentLiabilities?.total,
                              )}
                            </strong>
                          </td>
                        </tr>

                        <tr className="table-primary">
                          <td colSpan="2">
                            <strong>Total Current Liabilities</strong>
                          </td>
                          <td className="text-end">
                            <strong className="">
                              {formatToTwoDecimal(
                                liabilities?.totalCurrentLiabilities,
                              )}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Non-Current liabilities Section */}
                <div className="d-none col-sm mb-3">
                  <div className="w-100 d-flex align-items-center mt-4">
                    <h5>Non-Current Liabilities</h5>
                    <hr className="flex-grow-1 mx-3" />
                  </div>
                  <div className="w-100">
                    <table className="table table-bordered mt-4">
                      <thead>
                        <tr>
                          <th>Subject 1</th>
                          <th>Subject 2</th>
                          <th className="text-end">Amount (₱)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Long-Term Debt */}
                        <tr>
                          <td rowSpan="2">Long-Term Debt</td>
                          <td></td>
                          <td className="text-end peso">0.00</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end peso">
                            <strong className="text-success total-amount">
                              0.00
                            </strong>
                          </td>
                        </tr>

                        {/* Deferred Tax Liabilities
                         */}
                        <tr>
                          <td rowSpan="2">Deferred Tax Liabilities</td>
                          <td></td>
                          <td className="text-end peso">0.00</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount peso">
                              0.00
                            </strong>
                          </td>
                        </tr>

                        {/* Other Non-Current Liabilities
                         */}
                        <tr>
                          <td rowSpan="2">Other Non-Current Liabilities</td>
                          <td></td>
                          <td className="text-end peso">0.00</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td className="text-end">
                            <strong className="text-success total-amount peso">
                              0.00
                            </strong>
                          </td>
                        </tr>
                        {/* Total Non-Current Liabilities */}
                        <tr className="table-primary">
                          <td colSpan="2">
                            <strong>Total Non-Current Liabilities</strong>
                          </td>
                          <td className="text-end">
                            <strong className="peso">0.00</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="container-fluid">
              <div className="w-100 d-flex align-items-center mt-4">
                <h4 className="text-success">Owners' Equity Section</h4>
                <hr className="flex-grow-1 mx-3" />
              </div>
              {/* Owner Equity data here */}
              <div className="w-100">
                <table className="table table-bordered mt-4">
                  <thead>
                    <tr>
                      <th>Subject 1</th>
                      <th>Subject 2</th>
                      <th className="text-end">Amount (₱)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Capital */}
                    <tr>
                      <td rowSpan="2">Capital</td>
                      {/* <td></td>
                      <td className="text-end">
                        {formatToTwoDecimal(ownersEquity?.capital?.value)}
                      </td> */}
                    </tr>
                    <tr>
                      <td></td>
                      <td className="text-end">
                        <strong className="text-success total-amount">
                          {formatToTwoDecimal(ownersEquity?.capital?.total)}
                        </strong>
                      </td>
                    </tr>

                    {/* Retained Earnings */}
                    <tr>
                      <td rowSpan="4">Retained Earnings</td>
                      <td>Previous Profit</td>
                      <td className="text-end">
                        {formatToTwoDecimal(
                          ownersEquity?.retainedEarnings?.previousProfit,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Current Profit</td>
                      <td className="text-end">
                        {formatToTwoDecimal(
                          ownersEquity?.retainedEarnings?.currentProfit,
                        )}
                      </td>
                    </tr>
                    {/* Profit Distribution payouts to owners (negative) */}
                    <tr>
                      <td>Year End Distribution</td>
                      <td
                        className={`text-end ${
                          ownersEquity?.retainedEarnings?.yearEndDistribution
                            ? "text-danger"
                            : ""
                        }`}
                      >
                        {formatToTwoDecimal(
                          ownersEquity?.retainedEarnings?.yearEndDistribution,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td></td>
                      <td className="text-end">
                        <strong className="text-success total-amount">
                          {formatToTwoDecimal(
                            ownersEquity?.retainedEarnings?.total,
                          )}
                        </strong>
                      </td>
                    </tr>
                    {/* <tr>
                      <td rowSpan="2">Cost of Goods Sold (COGS)</td>
                      <td className="text-end">
                        {formatToTwoDecimal(ownersEquity?.cogs?.value)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-end">
                        <strong className="text-success total-amount">
                          {formatToTwoDecimal(ownersEquity?.cogs?.total)}
                        </strong>
                      </td>
                    </tr> */}

                    {/* Total Equity */}
                    <tr className="table-primary">
                      <td colSpan="2">
                        <strong>Total Equity</strong>
                      </td>
                      <td className="text-end">
                        <strong className="">
                          {formatToTwoDecimal(ownersEquity?.totalEquity)}
                        </strong>
                      </td>
                    </tr>
                    <tr className="table-primary">
                      <td colSpan="2">
                        {" "}
                        <strong>Total Liabilities and Equity</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {formatToTwoDecimal(
                            ownersEquity?.totalEquity +
                              liabilities.totalCurrentLiabilities,
                          )}
                        </strong>
                      </td>
                    </tr>
                    <tr className="table-primary">
                      <td colSpan="2">
                        <strong>Balance Checking</strong>
                      </td>
                      <td className="text-end">
                        <strong>
                          {/* {assets.totalCurrentAssets -
                            (ownersEquity?.totalEquity +
                              liabilities.totalCurrentLiabilities) <=
                          0.001
                            ? 0
                            : formatToTwoDecimal(
                                assets.totalCurrentAssets -
                                  (ownersEquity?.totalEquity +
                                    liabilities.totalCurrentLiabilities),
                              )} */}
                          {(() => {
                            const imbalance =
                              assets.totalCurrentAssets -
                              (ownersEquity?.totalEquity +
                                liabilities.totalCurrentLiabilities);

                            return Math.abs(imbalance) <= 0.001
                              ? 0
                              : formatToTwoDecimal(imbalance);
                          })()}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}
    </div>
  );
};

export default NewBalanceSheet;
